import React, { MutableRefObject, ReactElement, useRef } from "react";
import JSZip from "jszip";
import { errorToast } from "../Toasts";
import { toast } from "react-toastify";
import { ProgressBar } from "primereact/progressbar";
import { saveAs } from "file-saver";
import "./Downloader.css";
import { Button } from "primereact/button";

type ToastId = null | string | number;

export interface FileData {
    folder?: string;
    source: string;
    name: string;
}

export interface DownloaderProps {
    fileData: FileData[];
    zipName?: string;
    className?: string;
}

const handleToastProgressUpdate = (
    progress: number,
    progressPercent: number,
    filesDownloaded: number,
    totalFilesToDownload: number,
    toastId: MutableRefObject<ToastId>
) => {
    if (toastId.current) {
        toast.update(toastId.current, {
            progress: progress,
            render: () => (
                <div>
                    <p>{"Preparing files"}</p>
                    <ProgressBar
                        className="file-progress"
                        value={progressPercent}
                        displayValueTemplate={() => (
                            <span>{`${filesDownloaded}/${totalFilesToDownload}`}</span>
                        )}
                    ></ProgressBar>
                </div>
            )
        });
    }
};

const clearToast = (toastId: MutableRefObject<ToastId>) => {
    if (toastId.current) {
        toast.done(toastId.current);
    }
};

const downloadZip = async (
    files: FileData[],
    zipName: string,
    toastId: MutableRefObject<ToastId>
) => {
    try {
        const zip = new JSZip();
        toastId.current = toast("Preparing files", {
            type: "success",
            closeButton: false,
            closeOnClick: false,
            progressClassName: "toast-progress-bar",
            icon: (
                <i
                    className="pi pi-check"
                    style={{
                        color: "var(--toastify-icon-color-success)",
                        fontSize: "1em"
                    }}
                ></i>
            )
        });

        const totalFilesToDownload = files.length;
        let filesDownloaded = 0;
        const fileNames: string[] = [];

        for (const file of files) {
            let folder: JSZip | null = null;

            if (file.folder) {
                folder = zip.folder(file.folder);
            }

            let blob;
            try {
                const res = await fetch(file.source);
                blob = res.blob();
            } catch (err) {
                console.error(err);
                continue;
            }

            const progress = ++filesDownloaded / totalFilesToDownload;
            const progressPercent = Math.round(progress * 100);
            handleToastProgressUpdate(
                progress,
                progressPercent,
                filesDownloaded,
                totalFilesToDownload,
                toastId
            );
            const fileName = file.name;

            if (!fileNames.includes(fileName)) {
                (folder ?? zip).file(fileName, blob);
                fileNames.push(fileName);
            } else {
                let adjustIterator = 1;
                const fileNameWithoutExtension = fileName.substring(
                    0,
                    fileName.lastIndexOf(".")
                );
                const ext = fileName.substring(fileName.lastIndexOf("."));

                let adjustedFileName: string;
                do {
                    adjustedFileName = `${fileNameWithoutExtension}_${adjustIterator++}${ext}`;
                } while (fileNames.includes(adjustedFileName));
                (folder ?? zip).file(adjustedFileName, blob);
                fileNames.push(adjustedFileName);
            }
        }

        zip.generateAsync({ type: "blob" }).then(function (content) {
            saveAs(content, zipName);
        });
    } catch {
        errorToast("Download failed");
    } finally {
        clearToast(toastId);
    }
};

export const Downloader = (props: DownloaderProps): ReactElement => {
    const toastId = useRef<null | string | number>(null);
    const className = props.className;

    return (
        <Button
            icon={"pi pi-download"}
            className={`p-button-rounded p-button-text ${className}`}
            onClick={async () => {
                await downloadZip(props.fileData, props.zipName!, toastId);
            }}
        ></Button>
    );
};
